import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import ResidentPortrait from "@/components/ResidentPortrait";
import { PortraitEditor, PortraitGrid } from "@/components/PortraitPicker";
import { GardienPickerButton } from "@/components/GardienPicker";
import { AVATARS } from "@/data/exercises";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Check, RefreshCw, Play } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getGardien } from "@/data/gardiens";
import { GardienAvatar } from "@/components/GardienBadge";
import { InstallAppButton } from "@/components/InstallAppButton";

const KEY = "passerelle-home-aide";

export function useHomeAide() {
  const [patients, setPatients] = useState(null);
  const [selId, setSelId] = useState(() => localStorage.getItem(KEY));
  const load = useCallback(() => api.get("/patients").then((r) => setPatients(r.data)).catch(() => setPatients([])), []);
  useEffect(() => { load(); }, [load]);
  const aide = patients?.find((p) => p.id === selId) || patients?.[0] || null;
  const [paid, setPaid] = useState(null);
  useEffect(() => { api.get("/paypal/subscription").then((r) => setPaid(!!r.data.paid)).catch(() => {}); }, []);
  const select = (id) => { localStorage.setItem(KEY, id); setSelId(id); };
  return { patients, aide, select, load, paid };
}

export function HomeAideHero({ aide, canPatientMode }) {
  const navigate = useNavigate();
  if (!aide) return null;
  return (
    <div className="rounded-[2rem] bg-white border border-amber-100 px-6 py-8 mb-8 text-center" data-testid="home-aide-hero">
      <ResidentPortrait name={aide.name} avatar={aide.avatar} gardienId={aide.gardien_id} testId="home-aide-portrait" />
      {canPatientMode && <><Button className="mt-6 h-auto min-h-12 py-3 px-5 sm:px-7 text-base whitespace-normal max-w-full" onClick={() => navigate(`/patient/${aide.id}`)} data-testid="home-aide-patient-mode">
        <Play className="w-5 h-5 mr-2" /> Ouvrir le mode patient de {aide.name}
      </Button>
      <p className="text-sm text-gray-500 mt-2">Un écran tout simple, en grand, à donner à {aide.name} sur la tablette.</p></>}
      <div className="mt-4"><InstallAppButton className="!h-11 !px-5 !bg-white !text-emerald-800 border border-emerald-200 hover:!bg-emerald-50" /></div>
    </div>
  );
}

function CreateAide({ onCreated }) {
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState(AVATARS[0]);
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/patients", { name: name.trim(), avatar, condition: "", notes: "" });
      toast.success(`Le profil de ${data.name} est créé`);
      onCreated(data.id);
    } catch { toast.error("Création impossible."); }
    setBusy(false);
  };
  return (
    <div data-testid="home-aide-create">
      <h2 className="font-serif-display text-3xl mb-1">Créer le profil de votre proche</h2>
      <p className="text-gray-500 mb-6">La personne que vous accompagnez à la maison : son prénom et son portrait dans l'univers des Gardiens.</p>
      <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-2">Prénom</p>
      <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} className="max-w-sm mb-6" data-testid="home-aide-name-input" />
      <PortraitGrid value={avatar} onChange={setAvatar} />
      <Button className="mt-6" onClick={create} disabled={busy || !name.trim()} data-testid="home-aide-create-button">
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Créer le profil
      </Button>
    </div>
  );
}

export function HomeAideProfile({ patients, aide, select, load }) {
  return (
    <section className="rounded-3xl bg-white border border-border/60 p-6 sm:p-8" data-testid="home-aide-profile">
      {!aide ? (
        <CreateAide onCreated={async (id) => { select(id); await load(); }} />
      ) : (
        <>
          {patients.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-6" data-testid="home-aide-switch">
              {patients.map((p) => (
                <button key={p.id} onClick={() => select(p.id)} data-testid={`home-aide-select-${p.id}`}
                  className={`pl-1 pr-4 h-10 rounded-full text-sm font-semibold flex items-center gap-2 border ${p.id === aide.id ? "bg-emerald-700 text-white border-emerald-700" : "bg-white border-gray-200 text-gray-600"}`}>
                  <img src={p.avatar || AVATARS[0]} alt="" className="w-8 h-8 rounded-full object-cover object-top" /> {p.name}
                </button>
              ))}
            </div>
          )}
          <h2 className="font-serif-display text-3xl mb-1">Le profil de {aide.name}</h2>
          <p className="text-gray-500 mb-5">Son Gardien et son portrait dans l'univers des Gardiens.</p>
          <GardienPickerButton personName={aide.name} current={aide.gardien_id} testId="home-aide-gardien-button" className="mb-8"
            onSave={async (gid) => { await api.put(`/patients/${aide.id}/gardien`, { gardien_id: gid }); await load(); }} />
          <PortraitEditor key={aide.id} personName={aide.name} current={aide.avatar}
            onSave={async (a) => { await api.put(`/patients/${aide.id}/avatar`, { avatar: a }); await load(); }} />
        </>
      )}
    </section>
  );
}

const todayKey = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Paris" });
const dayIndex = () => Math.floor(Date.now() / 86400000);

export function HomeAideDailyActivity({ aide }) {
  const g = getGardien(aide?.gardien_id || "rose-auguste");
  const doneKey = `passerelle-activite-${aide?.id || "x"}-${todayKey()}`;
  const [shift, setShift] = useState(0);
  const [done, setDone] = useState(() => localStorage.getItem(doneKey) === "1");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDone(localStorage.getItem(doneKey) === "1"); setShift(0); }, [doneKey]);
  useEffect(() => {
    if (!aide?.id) return;
    api.get(`/patients/${aide.id}/home-activities`).then((r) => {
      if (r.data[0]?.date === todayKey()) setDone(true);
    }).catch(() => {});
  }, [aide?.id]);
  if (!aide) return null;
  const acts = g.activities;
  const activity = acts[(dayIndex() + shift) % acts.length];
  const markDone = async () => {
    setSaving(true);
    try { await api.post(`/patients/${aide.id}/home-activities`, { gardien_id: g.id, activity }); }
    catch { setSaving(false); toast.error("L'activité n'a pas pu être notée dans le carnet."); return; }
    setSaving(false); localStorage.setItem(doneKey, "1"); setDone(true); toast.success(`Bravo ! ${g.name} ${g.title.startsWith("Les ") ? "sont fiers" : g.title.startsWith("La ") ? "est fière" : "est fier"} de vous deux. C'est noté dans le carnet d'activités.`); };
  return (
    <section className="rounded-3xl bg-white border-2 p-6 sm:p-8 mb-8 flex flex-col sm:flex-row gap-6 items-start soft-in" style={{ borderColor: `${g.color}40` }} data-testid="home-daily-activity">
      <GardienAvatar id={g.id} className="w-24 h-24 shrink-0" />
      <div className="flex-1">
        <p className="text-xs uppercase tracking-[0.25em] font-semibold" style={{ color: g.color }}>L'activité du jour · proposée par {g.name}</p>
        <p className="font-serif-display text-2xl sm:text-3xl text-gray-800 mt-2 leading-snug" data-testid="home-daily-activity-text">{activity}</p>
        <p className="text-gray-500 mt-2">Un petit moment à partager avec {aide.name}, sans pression, quand vous en avez envie.</p>
        {done ? (
          <p className="mt-5 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold px-5 h-11" data-testid="home-daily-activity-done">
            <Check className="w-5 h-5" /> Fait aujourd'hui · « {g.quote} »
          </p>
        ) : (
          <div className="mt-5 flex flex-wrap gap-3">
            <Button onClick={markDone} disabled={saving} data-testid="home-daily-activity-done-button">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} C'est fait !</Button>
            {acts.length > 1 && <Button variant="outline" onClick={() => setShift((s) => s + 1)} data-testid="home-daily-activity-other"><RefreshCw className="w-4 h-4 mr-2" /> Une autre idée</Button>}
          </div>
        )}
      </div>
    </section>
  );
}
